import { useState, useRef, useEffect } from 'react';
import { Button } from 'components/common';
import { renderImage, renderParagraph } from 'components/utils/render';
import { pushClickCtaEvent, pushClickEvent, pushClickTileEvent } from '../../../tracking/events';
import { registerMixpanelSuperProperties } from '../../../tracking/mixpanel';
import { captureInboundAttribution, appendTileUtms, getTileHref, getTileId, isOutboundUrl } from '../../../tracking/tiles';
import PledgeForm, { buildPledgeFormFromSignup, readAwarenessPledge, writeAwarenessPledge, HIDE_FORM_AFTER_PLEDGE } from './PledgeForm';
import {
    AwarenessRaiserPageProps,
    TransformedButton,
    WayCard as WayCardData,
} from '../AwarenessRaiserPage/AwarenessRaiserPage.models';
import {
    HeroSection,
    HeroMedia,
    HeroInner,
    HeroTitle,
    PledgeSection,
    PledgeInner,
    PledgeHeading,
    PledgeGrid,
    PledgeItem,
    PledgeImage,
    PledgeCaption,
    PledgeFormSection,
    PledgeFormInner,
    PledgeFormGrid,
    PledgeFormCopy,
    PledgeFormReadMore,
    WaysSection,
    WaysInner,
    WaysEyebrow,
    WaysHeading,
    WaysGrid,
    WaysTile,
    WaysTileImage,
    WaysTileBody,
    WaysTileTitle,
    WaysTileCta,
    OtherWaysSection,
    OtherWaysInner,
    OtherWaysHeading,
    OtherWaysButtons,
    TransparencySection,
    TransparencyCopy,
    TransparencyEyebrow,
    TransparencyHeading,
    TransparencyBody,
    TransparencyCta,
    TransparencyMedia,
} from './AwarenessLanding.style';

/**
 * DynamicPage spreads Ali's transformer object into this page.
 * Hero, signup, benefits, ways, otherWays and transparency all come from CMS.
 * Empty sections are not rendered.
 */

const TILE_NAV_HOLD_MS = 250;

const hasImage = (image?: object) => !!image && Object.keys(image).length > 0;

const Chevron = () => (
    <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        <path d="M9 5l7 7-7 7" />
    </svg>
);

const AwarenessLanding: React.FC<AwarenessRaiserPageProps> = ({
    cms,
    image,
    mobileImage,
    heroTitle,
    signup,
    benefits,
    ways,
    otherWays,
    transparency,
}) => {
    const benefitItems = (benefits && benefits.items) || [];
    const wayCards = (ways && ways.cards) || [];
    const otherWaysButtons = (otherWays && otherWays.buttons) || [];

    const [hadPledged] = useState(readAwarenessPledge);
    const [hasSignedUp, setHasSignedUp] = useState(hadPledged);
    const [pledgedThisSession, setPledgedThisSession] = useState(false);
    const waysRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        captureInboundAttribution();
        if (hadPledged) {
            registerMixpanelSuperProperties({ awareness_raiser: true });
        }
    }, [hadPledged]);

    useEffect(() => {
        if (!hasSignedUp || !waysRef.current) {
            return;
        }
        const node = waysRef.current;
        const frame = window.requestAnimationFrame(() => {
            node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [hasSignedUp]);

    const onPledgeSuccess = () => {
        writeAwarenessPledge();
        setPledgedThisSession(true);
        setHasSignedUp(true);
    };

    const trackCta = (button: TransformedButton, location: string) => {
        pushClickCtaEvent({
            linkText: button.btnText,
            linkUrl: button.btnLink,
            location,
        });
    };

    const onTileClick = (event: React.MouseEvent<HTMLAnchorElement>, card: WayCardData, index: number) => {
        const destination = getTileHref(card);
        if (!card.cta || !destination) return;

        const tileId = getTileId(card);
        const destinationUrl = appendTileUtms(destination, tileId);
        const outbound = isOutboundUrl(destinationUrl);

        pushClickTileEvent({
            linkText: card.cta.btnText,
            linkUrl: destinationUrl,
            location: 'six_ways',
            tileId,
            position: index + 1,
            isOutbound: outbound,
            pledgeContext: pledgedThisSession ? 'this_session' : 'returning',
        });

        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        event.preventDefault();
        const openInNewTab = card.cta.btnType === 'external';
        window.setTimeout(() => {
            if (openInNewTab) {
                window.open(destinationUrl, '_blank', 'noopener');
            } else {
                window.location.href = destinationUrl;
            }
        }, TILE_NAV_HOLD_MS);
    };

    return (
        <>
            {(hasImage(image) || heroTitle) &&
                <HeroSection>
                    {hasImage(image) &&
                        <HeroMedia>{renderImage(image)}</HeroMedia>
                    }
                    {hasImage(mobileImage)
                        ? <HeroMedia isMobileOnly={true}>{renderImage(mobileImage)}</HeroMedia>
                        : hasImage(image) && <HeroMedia isMobileOnly={true}>{renderImage(image)}</HeroMedia>
                    }
                    <HeroInner>
                        {heroTitle && <HeroTitle>{heroTitle}</HeroTitle>}
                    </HeroInner>
                </HeroSection>
            }

            {/* The copy invites a sign up, so it goes with the form rather
                than sitting above an empty column. */}
            {signup && !(HIDE_FORM_AFTER_PLEDGE && hasSignedUp) &&
                <PledgeFormSection>
                    <PledgeFormInner>
                        <PledgeFormGrid>
                            <PledgeFormCopy>
                                {signup.introText && signup.introText.length > 0 &&
                                    renderParagraph(signup.introText)
                                }
                                {signup.readMoreCta && signup.readMoreCta.btnText && signup.readMoreCta.btnLink &&
                                    <PledgeFormReadMore
                                        href={signup.readMoreCta.btnLink}
                                        onClick={() => pushClickEvent({
                                            linkText: signup.readMoreCta ? signup.readMoreCta.btnText : 'Read More',
                                            linkUrl: signup.readMoreCta ? signup.readMoreCta.btnLink : undefined,
                                        })}
                                    >
                                        {signup.readMoreCta.btnText}
                                        <Chevron />
                                    </PledgeFormReadMore>
                                }
                            </PledgeFormCopy>
                            <PledgeForm
                                cms={cms}
                                {...buildPledgeFormFromSignup(signup)}
                                hadPledged={hadPledged}
                                onSuccess={onPledgeSuccess}
                            />
                        </PledgeFormGrid>
                    </PledgeFormInner>
                </PledgeFormSection>
            }

            {benefitItems.length > 0 &&
                <PledgeSection>
                    <PledgeInner>
                        {benefits && benefits.heading &&
                            <PledgeHeading>{benefits.heading}</PledgeHeading>
                        }
                        <PledgeGrid>
                            {benefitItems.map((item, index) =>
                                <PledgeItem key={index}>
                                    {hasImage(item.image) &&
                                        <PledgeImage>{renderImage(item.image)}</PledgeImage>
                                    }
                                    {item.text && <PledgeCaption>{item.text}</PledgeCaption>}
                                </PledgeItem>
                            )}
                        </PledgeGrid>
                    </PledgeInner>
                </PledgeSection>
            }

            {hasSignedUp && wayCards.length > 0 &&
                <WaysSection ref={waysRef}>
                    <WaysInner>
                        {ways && ways.eyebrow && <WaysEyebrow>{ways.eyebrow}</WaysEyebrow>}
                        {ways && ways.heading && <WaysHeading>{ways.heading}</WaysHeading>}
                        <WaysGrid>
                            {wayCards.map((card, index) =>
                                <WaysTile
                                    key={index}
                                    href={card.cta ? appendTileUtms(getTileHref(card), getTileId(card)) : undefined}
                                    aria-label={card.cta ? `${card.title} — ${card.cta.btnText}` : card.title}
                                    data-tile-id={getTileId(card)}
                                    onClick={event => onTileClick(event, card, index)}
                                    {...(card.cta && card.cta.btnType === 'external'
                                        ? { target: '_blank', rel: 'noopener' }
                                        : {})}
                                >
                                    {hasImage(card.image) &&
                                        <WaysTileImage>{renderImage(card.image)}</WaysTileImage>
                                    }
                                    <WaysTileBody>
                                        {card.title && <WaysTileTitle>{card.title}</WaysTileTitle>}
                                        {card.cta && card.cta.btnText &&
                                            <WaysTileCta>
                                                {card.cta.btnText}
                                                <Chevron />
                                            </WaysTileCta>
                                        }
                                    </WaysTileBody>
                                </WaysTile>
                            )}
                        </WaysGrid>
                    </WaysInner>
                </WaysSection>
            }

            {otherWays && (otherWays.heading || otherWaysButtons.length > 0) &&
                <OtherWaysSection
                    aria-labelledby={otherWays.heading ? 'awareness-other-ways-heading' : undefined}
                >
                    <OtherWaysInner>
                        {otherWays.heading &&
                            <OtherWaysHeading id="awareness-other-ways-heading">
                                {otherWays.heading}
                            </OtherWaysHeading>
                        }
                        {otherWaysButtons.length > 0 &&
                            <OtherWaysButtons>
                                {otherWaysButtons.map((button, index) =>
                                    button && button.btnText
                                        ? <Button
                                            key={index}
                                            {...button}
                                            onClickCapture={() => trackCta(button, 'other_ways')}
                                        />
                                        : null
                                )}
                            </OtherWaysButtons>
                        }
                    </OtherWaysInner>
                </OtherWaysSection>
            }

            {transparency &&
                <TransparencySection
                    $hasMedia={hasImage(transparency.image)}
                    aria-labelledby={transparency.heading ? 'awareness-transparency-heading' : undefined}
                >
                    <TransparencyCopy>
                        {transparency.eyebrow &&
                            <TransparencyEyebrow>{transparency.eyebrow}</TransparencyEyebrow>
                        }
                        {transparency.heading &&
                            <TransparencyHeading id="awareness-transparency-heading">
                                {transparency.heading}
                            </TransparencyHeading>
                        }
                        {transparency.description && transparency.description.length > 0 &&
                            <TransparencyBody>{renderParagraph(transparency.description)}</TransparencyBody>
                        }
                        {transparency.button && transparency.button.btnText &&
                            <TransparencyCta>
                                <Button
                                    {...transparency.button}
                                    onClickCapture={() => trackCta(transparency.button as TransformedButton, 'transparency')}
                                />
                            </TransparencyCta>
                        }
                    </TransparencyCopy>
                    {hasImage(transparency.image) &&
                        <TransparencyMedia>{renderImage(transparency.image)}</TransparencyMedia>
                    }
                </TransparencySection>
            }
        </>
    );
};

export default AwarenessLanding;
